import * as React from 'react';
import { useCallback, useRef, useState } from 'react';
import type { TextOnPathProps } from '../../TextOnPath.types';
import type { TextOnPathSvgProps } from './TextOnPathSvg';
import classNames from 'clsx';
import { PauseSmall, PlaySmall } from '@wix/wix-ui-icons-common';
import styles from '../TextOnPath.scss';
import { ViewerTestIds } from '../../constants';

type PauseReason = 'click' | 'hover' | null;

type TextOnPathClientProps = Pick<TextOnPathSvgProps, 'isAnimated' | 'link'> &
  Pick<TextOnPathProps, 'translations'> & {
    pauseOnHover: boolean;
    children: React.ReactNode;
  };

export function TextOnPathA11yWrapper({
  translations,
  pauseOnHover,
  children,
  isAnimated,
}: TextOnPathClientProps) {
  const [isAnimationPaused, setIsAnimationPaused] = useState(false);

  const wrapperDivRef = useRef<HTMLDivElement>(null);
  const pauseReasonRef = useRef<PauseReason>(null);

  const svgRef = () => {
    return wrapperDivRef.current?.querySelector(
      '[data-svg-ref]',
    ) as SVGSVGElement;
  };

  const pauseAnimation = useCallback((reason: PauseReason) => {
    const svg = svgRef();
    if (!svg) {
      return;
    }

    try {
      svg.pauseAnimations();
      pauseReasonRef.current = reason;
      setIsAnimationPaused(true);
    } catch (error) {
      // Fail silently if animation methods are not supported
    }
  }, []);

  const resumeAnimation = useCallback((reason: PauseReason) => {
    const svg = svgRef();
    if (!svg || pauseReasonRef.current !== reason) {
      return;
    }

    try {
      svg.unpauseAnimations();
      pauseReasonRef.current = null;
      setIsAnimationPaused(false);
    } catch (error) {
      // Fail silently if animation methods are not supported
    }
  }, []);

  const mouseEnterHandler = () => {
    if (!pauseReasonRef.current) {
      pauseAnimation('hover');
    }
  };

  const mouseLeaveHandler = () => {
    resumeAnimation('hover');
  };

  const handleClick = () => {
    if (!isAnimated) {
      return;
    }

    if (pauseReasonRef.current === 'click') {
      resumeAnimation('click');
    } else {
      pauseAnimation('click');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.code === 'Space') {
      handleClick();
      e.preventDefault();
    }
  };

  return (
    <div
      onMouseEnter={pauseOnHover ? mouseEnterHandler : undefined}
      onMouseLeave={pauseOnHover ? mouseLeaveHandler : undefined}
      onClick={handleClick}
      ref={wrapperDivRef}
      data-testid={ViewerTestIds.a11yRoot}
      className={styles.textOnPathA11yWrapper}
    >
      {children}
      {isAnimated && (
        <button
          className={classNames(styles.playButton, styles.visuallyHidden)}
          onKeyDown={handleKeyDown}
          aria-label={
            isAnimationPaused ? translations.playLabel : translations.pauseLabel
          }
          aria-pressed={isAnimationPaused}
        >
          {isAnimationPaused ? (
            <>
              <span>{translations.playLabel}</span>
              <PlaySmall />
            </>
          ) : (
            <>
              <span>{translations.pauseLabel}</span>
              <PauseSmall />
            </>
          )}
        </button>
      )}
    </div>
  );
}
